// ============================================================================
// phytoedit_scoring_client.js
//
// Replaces the client-side scoring functions in PlantCRISPR_Designer.jsx with
// calls to the server-side scoring service, which runs the reference
// implementations (rs3 for RS3; CRISPOR for RS2/CRISPRscan/Chari/Wang).
//
// The functions being replaced -- scoreRS3, scoreCRISPRon, scoreDoenchRS2,
// scoreCRISPRscan, scoreCFDSpecificity, computeEnsembleScore -- were hand
// written approximations, not the published algorithms. Verified against the
// reference implementations they agreed at rho = 0.634 (RS3), 0.577
// (CRISPRon), 0.560 (RS2) and 0.130 (CRISPRscan).
//
// Coordinate geometry, from findGRNAs() with pamSide === "3prime":
//   plus  strand: protospacer at seq[start, start+20), PAM at seq[pamStart, +3)
//   minus strand: forward sequence reads [PAM_rc 3nt][protospacer_rc 20nt]
//                 starting at pamStart, so contexts are built from pamStart
//                 and reverse-complemented.
// ============================================================================

const SCORE_ENDPOINT = "/api/score/batch";
const STATUS_ENDPOINT = "/api/score/status";

// Gene sequences must be fetched with &pad=100 so guides near either end
// still have flanking sequence. Anything less and the 100-mer cannot be built.
export const REQUIRED_PAD = 100;

function rc(s) {
  const M = { A: "T", C: "G", G: "C", T: "A", N: "N" };
  let out = "";
  for (let i = s.length - 1; i >= 0; i--) out += M[s[i]] || "N";
  return out;
}

/**
 * Build the 30-mer and 100-mer genomic contexts for one guide.
 * Returns {context30, context100}; either may be null if the padded
 * sequence does not extend far enough. Never fabricates or pads with N.
 */
export function buildContexts(g, padded, padLeft) {
  if (!padded || padLeft == null) return { context30: null, context100: null };

  const take = (from, to) =>
    from >= 0 && to <= padded.length ? padded.slice(from, to) : null;

  if (g.strand === "+") {
    const ps = padLeft + g.start;
    const c30 = take(ps - 4, ps + 26);
    const c100 = take(ps - 30, ps + 70);
    return { context30: c30, context100: c100 };
  }

  // minus strand: anchor on the PAM position in forward coordinates
  const pm = padLeft + g.pamStart;
  const f30 = take(pm - 3, pm + 27);
  const f100 = take(pm - 47, pm + 53);
  return { context30: f30 ? rc(f30) : null, context100: f100 ? rc(f100) : null };
}

/** Which algorithms this deployment can actually serve. */
export async function fetchScorerStatus(baseUrl = "") {
  try {
    const r = await fetch(`${baseUrl}${STATUS_ENDPOINT}`, {
      signal: AbortSignal.timeout(10000),
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

/**
 * Score guides server-side.
 *
 * @param {Array}  guides  output of findGRNAs()
 * @param {Object} gene    {paddedSequence, padLeft, ...} from /api/gene?pad=100
 * @param {Object} extras  optional per-uid {domain_hit, frac_cds_position,
 *                         dist_from_atg, frameshift_prob, caps_available}
 * @returns {Map} uid -> score object, plus `_status` on the returned Map
 */
export async function scoreGuides(guides, gene, extras = {}, baseUrl = "") {
  const out = new Map();
  if (!guides || guides.length === 0) return out;

  const padded = gene?.paddedSequence || null;
  const padLeft = gene?.padLeft ?? null;

  const payload = guides.map((g) => {
    const { context30, context100 } = buildContexts(g, padded, padLeft);
    const e = extras[g.uid] || {};
    return {
      spacer: g.guide,
      pam_context: g.pam || "",
      ...(context30 ? { context30 } : {}),
      ...(context100 ? { context100 } : {}),
      ...e,
    };
  });

  // The service caps batches; chunk to stay under it.
  const CHUNK = 400;
  let status = null;
  for (let i = 0; i < payload.length; i += CHUNK) {
    const slice = payload.slice(i, i + CHUNK);
    let json;
    try {
      const r = await fetch(`${baseUrl}${SCORE_ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guides: slice }),
        signal: AbortSignal.timeout(120000),
      });
      if (!r.ok) throw new Error(`scoring service returned ${r.status}`);
      json = await r.json();
    } catch (err) {
      // Fail visibly: leave scores undefined rather than substituting
      // a local approximation. The UI must show these as unavailable.
      for (let k = 0; k < slice.length; k++) {
        out.set(guides[i + k].uid, { _error: String(err.message || err) });
      }
      continue;
    }
    status = json.scorer_status || status;
    (json.results || []).forEach((res, k) => {
      out.set(guides[i + k].uid, res);
    });
  }
  out._status = status;
  return out;
}

/**
 * Shape a service result into the `scores` object the UI already renders.
 * Names deliberately differ from the old ones: `phytoscore_activity` is not
 * the old `holistic` score (the cloning and genotyping terms were moved out
 * of it), and there is no ensemble.
 */
export function toUiScores(res, guide) {
  if (!res || res._error) {
    return { unavailable: true, error: res?._error || "not scored" };
  }
  return {
    phytoScore: res.phytoscore_activity ?? null,
    phytoImpact: res.phytoscore_impact ?? null,
    rs3: res.RS3 ?? null,
    rs2: res.RS2 ?? null,
    crisprscan: res.CRISPRscan ?? null,
    chari: res.Chari ?? null,
    wang: res.Wang ?? null,
    crispron: res.CRISPRon ?? null, // null on deployments without the model
    designFlags: res.design_flags || {},
    contextWarning: res.context_warning || null,
    gc: guide ? Math.round(
      (100 * (guide.guide.match(/[GC]/g) || []).length) / guide.guide.length) : null,
  };
}
